import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Button,
} from "@chakra-ui/react";
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ComposedChart,
  TooltipProps,
} from "recharts";

import { DateTime } from "luxon";

// const currentWeekDates = (() => {
//   const startOfWeek = DateTime.local().startOf("week");

//   const map: Record<string, string> = {};

//   for (let i = 0; i < 7; i++) {
//     const date = startOfWeek.plus({ days: i });

//     const dayLabel = date.toFormat("cccc");

//     const dateLabel = date.toFormat("dd/MM/yy");

//     map[dayLabel] = `${dayLabel} (${dateLabel})`;
//   }

//   return map;
// })();

const getWeekDates = (offsetWeeks = 0) => {
  const startOfWeek = DateTime.local()
    .plus({ weeks: offsetWeeks })
    .startOf("week");

  const map: Record<string, string> = {};

  for (let i = 0; i < 7; i++) {
    const date = startOfWeek.plus({ days: i });
    const dayLabel = date.toFormat("cccc"); // Monday, Tuesday, etc.
    const dateLabel = date.toFormat("dd/MM/yy"); // 21/04/25
    map[dayLabel] = `${dayLabel} (${dateLabel})`;
  }

  return map;
};

const WEEKDAY_MAP: any = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

const timeToFloat = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h + m / 60;
};

const floatToTime = (decimal: number) => {
  const hours = Math.floor(decimal);
  const minutes = Math.round((decimal - hours) * 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

type TimeSlot = { start: string; end: string };

const ExpertWeeklyCalendarModal = ({
  isOpen,
  onClose,
  timing,
  currentBookings,
  nextBookings,
}: any) => {
  const [isCurrentWeek, setIsCurrentWeek] = useState(true); // toggle state

  const selectedBookings = isCurrentWeek ? currentBookings : nextBookings;

  const weekDates = getWeekDates(isCurrentWeek ? 0 : 1);

  const handleToggleWeek = () => {
    setIsCurrentWeek(!isCurrentWeek);
  };

  const data = Object.entries(timing || {}).map(
    ([dayKey, slots]: [string, any]) => {
      const rawSlots = (slots as TimeSlot[])
        .filter((slot) => timeToFloat(slot.end) > timeToFloat(slot.start))
        .map((slot) => ({
          start: timeToFloat(slot.start),
          end: timeToFloat(slot.end),
          label: `${slot.start} - ${slot.end}`,
        }))
        .sort((a, b) => a.start - b.start);

      const segments: any = { day: WEEKDAY_MAP[dayKey] };
      let current = 0;
      let index = 0;

      for (let slot of rawSlots) {
        if (slot.start > current) {
          segments[`w${index}`] = slot.start - current; // white segment
          current = slot.start;
          index++;
        }
        segments[`g${index}`] = slot.end - slot.start; // green segment
        segments[`timingLabel${index}`] = slot.label;
        current = slot.end;
        index++;
      }

      const dayBookings = selectedBookings.filter(
        (b: any) => b.day === WEEKDAY_MAP[dayKey]
      );

      const sortedBookings = [...dayBookings].sort((a, b) => a.start - b.start);
      let currentBook = 0;
      let bookingIndex = 0;

      for (let booking of sortedBookings) {
        if (booking.start > currentBook) {
          segments[`t${bookingIndex}`] = booking.start - currentBook; // transparent
          currentBook = booking.start;
          bookingIndex++;
        }
        segments[`o${bookingIndex}`] = booking.duration; // orange

        segments[`sessionLabel${bookingIndex}`] = `${floatToTime(
          booking.start
        )} - ${floatToTime(booking.end)}`;

        currentBook += booking.duration;
        bookingIndex++;
      }

      return segments;
    }
  );

  const CustomTooltip = ({ active, payload }: TooltipProps<any, string>) => {
    if (!active || !payload || !payload.length) return null;

    const rows = payload
      .map((entry: any) => {
        const key = entry.dataKey;
        const index = key.replace(/[^\d]/g, "");

        if (key.startsWith("g")) {
          const label = entry.payload[`timingLabel${index}`];
          return label
            ? {
                label,
                type: "Timings",
                color: entry.color || "#327171", // green
              }
            : null;
        }

        if (key.startsWith("o")) {
          const label = entry.payload[`sessionLabel${index}`];
          return label
            ? {
                label,
                type: "Sessions",
                color: entry.color || "#f48a0f", // orange
              }
            : null;
        }

        return null;
      })
      .filter(Boolean);

    if (rows.length === 0) return null;

    return (
      <div className="bg-white border p-2 rounded-md shadow-md text-sm space-y-1">
        {rows.map((row: any, i) => (
          <div key={i} className="flex items-center space-x-2">
            <span
              style={{
                color: row.color,
                fontWeight: 600,
                fontSize: "14px",
                marginRight: "2px",
              }}
            >
              {row.type}:
            </span>
            <span
              style={{ color: row.color, fontSize: "14px", marginRight: "2px" }}
            >
              {row.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xxl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justify="start" align="center" gap={5}>
            <span>Expert Weekly Timings</span>
            <Button
              className="btn action-btn"
              size="sm"
              onClick={handleToggleWeek}
            >
              {isCurrentWeek ? "Next Week" : "Current Week"}
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {data.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                layout="vertical"
                data={data}
                width={700}
                margin={{ top: 20, right: 30, bottom: 20, left: 100 }}
              >
                <XAxis
                  type="number"
                  domain={[0, 24]}
                  ticks={Array.from({ length: 25 }, (_, i) => i)}
                  tickFormatter={(t) => `${t}:00`}
                />

                <YAxis
                  yAxisId="left"
                  type="category"
                  dataKey="day"
                  width={10}
                  tickFormatter={(value) => weekDates[value] || value}
                />

                <YAxis
                  yAxisId="right"
                  type="category"
                  dataKey="day"
                  hide={true}
                />

                <Tooltip content={<CustomTooltip />} />

                {/* <Tooltip
                  formatter={(value, name, props: any) => {
                    const key = props.dataKey;
                    const index = key.replace(/[^\d]/g, "");

                    if (key.startsWith("g")) {
                      const label = props.payload[`timingLabel${index}`];
                      return label ? [label, "Timings"] : null;
                    }

                    if (key.startsWith("o")) {
                      const label = props.payload[`sessionLabel${index}`];
                      return label ? [label, "Sessions"] : null;
                    }

                    return null;
                  }}
                  filterNull={true}
                /> */}

                {Array.from({ length: 20 }).map((_, i) => (
                  <React.Fragment key={i}>
                    <Bar
                      yAxisId="left"
                      dataKey={`w${i}`}
                      stackId="a"
                      fill="#fff"
                    />
                    <Bar
                      yAxisId="left"
                      dataKey={`g${i}`}
                      stackId="a"
                      fill="#327171"
                    />

                    <Bar
                      yAxisId="right"
                      dataKey={`t${i}`}
                      stackId="b"
                      fill="transparent"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey={`o${i}`}
                      stackId="b"
                      fill="#f48a0f"
                    />
                  </React.Fragment>
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p>No timings available</p>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ExpertWeeklyCalendarModal;
